// components/sections/ConcursosCarrossel.tsx

'use client';

import React, { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import HighlightedHeading from "@/components/molecules/HighlightedHeading";
import { getHighlightPositions } from "@/lib/utils";


interface Concurso {
    id: string;
    medida: string;
    status: string;
    data: string;
    titulo: string;
    edital: string;
    descricao?: string;
    imagem?: string;
    link?: string;
}

interface ConcursosCarrosselProps {
    titulo?: string;
    subtitulo?: string;
    descricaoHeader?: string;
    concursos?: Concurso[];
    botaoMais?: string;
    botaoMaisLink?: string;
    onConcursoClick?: (concursoId: string) => void;
}



export default function ConcursosCarrossel({
    titulo = "Fica a par de todos os",
    subtitulo = "concursos",
    descricaoHeader = "Com transparência e rigor, garantimos o acesso à informação e incentivamos a participação de todos os interessados. Consulta os detalhes de cada concurso, prazos, regulamentos e formas de candidatura.",
    concursos = [],
    botaoMais = "+ Concursos",
    botaoMaisLink = "/concursos",
    onConcursoClick,
}: ConcursosCarrosselProps) {
    const [hoveredId, setHoveredId] = useState<string | null>(concursos[0]?.id || null);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [touchStart, setTouchStart] = useState<number | null>(null);
    const [touchEnd, setTouchEnd] = useState<number | null>(null);

    const handleConcursoClick = (concursoId: string) => {
        if (onConcursoClick) {
            onConcursoClick(concursoId);
        }
    };

    // Mobile: 1 card por slide, Tablet/1024px: 2 cards, Desktop 1280px+: 3 cards
    const getCardsPerSlide = () => {
        if (typeof window === 'undefined') return 3;
        if (window.innerWidth < 768) return 1; // mobile
        if (window.innerWidth < 1280) return 2; // tablet e 1024px
        return 3; // desktop 1280px+
    };

    const [cardsPerSlide, setCardsPerSlide] = useState(3);

    React.useEffect(() => {
        const handleResize = () => {
            setCardsPerSlide(getCardsPerSlide());
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const totalSlides = Math.ceil(concursos.length / cardsPerSlide);
    const currentConcursos = concursos.slice(
        currentSlide * cardsPerSlide,
        (currentSlide + 1) * cardsPerSlide
    );

    // Distância mínima de swipe (em px)
    const minSwipeDistance = 50;

    const onTouchStart = (e: React.TouchEvent) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e: React.TouchEvent) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;

        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isLeftSwipe && currentSlide < totalSlides - 1) {
            setCurrentSlide(currentSlide + 1);
        }
        if (isRightSwipe && currentSlide > 0) {
            setCurrentSlide(currentSlide - 1);
        }
    };

    return (
        <section className="w-full py-12 sm:py-14 md:py-16 bg-white mt-16 sm:mt-20 md:mt-24">
            <div className="container mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16">
                {/* Header Section */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 md:gap-10 lg:gap-12 mb-10 sm:mb-12 md:mb-16">
                    {/* Left - Title */}
                    <div className="max-w-full lg:max-w-[560px]">


                        {(() => {
                            const sentence = titulo || "";
                            const keywords = subtitulo || "";
                            const positions = getHighlightPositions(sentence, keywords);
                            return (
                                <HighlightedHeading
                                    text={sentence}
                                    positions={positions}

                                />
                            );
                        })()}

                    </div>

                    {/* Right - Description */}
                    <div className="text-sm sm:text-base md:text-lg text-gray-800 font-nunito leading-relaxed" dangerouslySetInnerHTML={{ __html: descricaoHeader }} />
                </div>

                {/* Carousel Container */}
                <div
                    className="relative mb-12 sm:mb-14 md:mb-16"
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                >
                    {/* Cards Container - Responsivo: 1 coluna mobile, 2 tablet/1024px (768-1279px), 3 desktop (1280px+) */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5 lg:gap-6 mb-8 sm:mb-10 md:mb-12">
                        {currentConcursos.map((concurso) => {
                            const isHovered = hoveredId === concurso.id;

                            return (
                                <div
                                    key={concurso.id}
                                    className="w-full transition-all duration-300 ease-in-out"
                                    onMouseEnter={() => setHoveredId(concurso.id)}
                                    onMouseLeave={() => setHoveredId(concursos[0]?.id || null)}
                                >
                                    <ConcursoCard
                                        concurso={concurso}
                                        isHovered={isHovered}
                                        onClick={() => handleConcursoClick(concurso.id)}
                                    />
                                </div>
                            );
                        })}
                    </div>

                    {/* Navigation Dots */}
                    {totalSlides > 1 && (
                        <div className="flex justify-center items-center gap-3 mb-12">
                            {Array.from({ length: totalSlides }).map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setCurrentSlide(index)}
                                    className={`transition-all duration-300 h-1 rounded-full cursor-pointer ${index === currentSlide
                                        ? "w-20 bg-gradient-to-r from-orange-600 via-orange-500 to-red-500"
                                        : "w-10 bg-gray-300 hover:bg-gray-400"
                                        }`}
                                    aria-label={`Ir para slide ${index + 1}`}
                                />
                            ))}
                        </div>
                    )}

                    {/* Button Mais Concursos */}
                    <div className="flex justify-center">
                        <Link href={botaoMaisLink}>
                            <button className="px-6 sm:px-7 py-2.5 sm:py-3 rounded-sm bg-gradient-to-r from-red-500 via-orange-600 to-amber-500 transition-opacity inline-flex justify-center items-center gap-2 hover:opacity-80 cursor-pointer w-full sm:w-auto min-w-[200px]">
                                <span className="text-white text-sm sm:text-base md:text-lg font-medium font-poppins">
                                    {botaoMais}
                                </span>
                            </button>
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    );
}

interface ConcursoCardProps {
    concurso: Concurso;
    isHovered: boolean;
    onClick: () => void;
}

function ConcursoCard({
    concurso,
    isHovered,
    onClick,
}: ConcursoCardProps) {
    const [windowWidth, setWindowWidth] = React.useState(0);

    React.useEffect(() => {
        // Inicializar com o tamanho atual da janela
        setWindowWidth(window.innerWidth);

        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // No mobile/tablet sempre mostra imagem, no desktop apenas o card com hover
    const isMobile = windowWidth > 0 && windowWidth < 1024;
    const shouldShowImage = isMobile || isHovered;


    return (
        <Link href={concurso.link || "#"}>
            <div
                className={`bg-white rounded-sm shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer h-auto min-h-[320px] sm:min-h-[360px] lg:h-96 p-4 sm:p-5 md:p-6 flex w-full ${isMobile ? 'flex-row' : shouldShowImage ? 'flex-row' : 'flex-col'}`}
                onClick={onClick}
            >
                {shouldShowImage ? (
                    <>
                        {/* Conteúdo Esquerdo - Expandido */}
                        <div className="flex-1 flex flex-col gap-3 sm:gap-4 lg:gap-6 justify-between">
                            {/* Tags */}
                            <div className="flex gap-2 flex-wrap">
                                <span className="px-2 sm:px-3 py-1 border border-gray-400 rounded-xs text-gray-600 text-xs sm:text-sm font-nunito">
                                    {concurso?.medida || "Medida I"}
                                </span>
                                <span className="px-2 sm:px-3 py-1 border border-orange-600 rounded-xs text-[#242424] text-xs sm:text-sm font-nunito">
                                    {concurso.status}
                                </span>
                            </div>

                            {/* Content Section */}
                            <div className="flex flex-col gap-3 sm:gap-4">
                                {/* Data e Título */}
                                <div className="flex flex-col gap-2">
                                    <p className="text-orange-500 text-xs sm:text-sm lg:text-base font-nunito-sans font-normal">
                                        {concurso.data}
                                    </p>

                                    <h3 className="text-gray-900 text-sm sm:text-base lg:text-lg xl:text-xl font-nunito font-medium leading-tight">
                                        {concurso.titulo}
                                        <br />
                                        {concurso.edital}
                                    </h3>
                                </div>

                                {/* Descrição - apenas desktop com hover */}
                                {!isMobile && isHovered && concurso.descricao && (
                                    <div className="text-gray-700 text-xs lg:text-sm font-nunito max-w-[200px] font-light leading-relaxed line-clamp-3" dangerouslySetInnerHTML={{ __html: concurso.descricao }} />
                                )}
                            </div>

                            {/* Link Detalhes */}
                            <span className="text-orange-500 text-xs sm:text-sm lg:text-base font-poppins font-normal">
                                + detalhes
                            </span>
                        </div>

                        {/* Imagem Direita */}
                        {concurso.imagem && (
                            <div className="w-32 sm:w-36 lg:w-40 xl:w-48 min-h-[200px] h-full shrink-0 rounded-xs overflow-hidden ml-3 sm:ml-4">
                                <Image
                                    src={concurso.imagem}
                                    alt={concurso.titulo}
                                    width={456}
                                    height={800}
                                    className="w-full h-full object-cover"
                                    quality={90}
                                />
                            </div>
                        )}
                    </>
                ) : (
                    <>
                        {/* Conteúdo Compactado - Desktop sem hover */}
                        <div className="flex flex-col gap-4 lg:gap-6 flex-1 justify-between">
                            {/* Tags */}
                            <div className="flex gap-2 flex-wrap">
                                <span className="px-3 py-1 border border-gray-400 rounded-xs text-gray-600 text-xs lg:text-sm font-nunito">
                                    {concurso?.medida || "Medida I"}
                                </span>
                                <span className="px-3 py-1 border border-orange-600 rounded-xs text-[#242424] text-xs lg:text-sm font-nunito">
                                    {concurso.status || "Em andamento"}
                                </span>
                            </div>

                            {/* Data e Título */}
                            <div className="flex flex-col gap-2">
                                <p className="text-orange-500 text-sm lg:text-base font-nunito-sans font-normal">
                                    {concurso.data}
                                </p>

                                <h3 className="text-gray-900 text-base lg:text-lg font-nunito font-medium leading-tight">
                                    {concurso.titulo}
                                    <br />
                                    {concurso.edital}
                                </h3>
                            </div>

                            {/* Link Detalhes */}
                            <span className="text-orange-500 text-sm lg:text-base font-poppins font-normal">
                                + detalhes
                            </span>
                        </div>
                    </>
                )}
            </div>
        </Link>
    );
}